import React, { Component } from 'react';
import { defineMessages, injectIntl, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';

import intlShape from '@/shared/utils/intlShape';
import RelationTypeIcon from '@/shared/components/RelationTypeIcon';
import { getRelationNameMapper } from '@/shared/components/RelationName';
import RecentRelationsService from '@/shared/services/RecentRelationsService';
import AutoComplete from './select/AutoComplete';
import config from '../../../config';
import api from '../../api';
import RelationHandlingLevel from '@/shared/components/RelationHandlingLevel';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const messages = defineMessages({
  SearchRelation: 'Search relation',
});

class RelationSearch extends Component {
  state = {
    initialRelation: null,
    handlingLevel: null,
    retrievingRelation: false,
  };

  constructor(props) {
    super(props);
    this.nameMapper = getRelationNameMapper(props.intl);
  }

  mapRelation = item => {
    // Ensure nameMapper is available, recreate if needed (React 18 Strict Mode compatibility)
    if (!this.nameMapper) {
      this.nameMapper = getRelationNameMapper(this.props.intl);
    }
    return {
      key: item.id,
      label: this.nameMapper(item),
      relationHandlingLevel: null,
      relationHandlingLevelClarification: null,
      ...item,
    };
  };
  mapHandlingLevel = item => {
    if (!item) return null;
    const id = item.id ?? item.key;
    const { relationHandlingLevel = null, relationHandlingLevelClarification = null } = item;
    if (!id) return null;
    const { handlingLevel } = this.state;
    if (handlingLevel && handlingLevel.id === id) return handlingLevel; //Same relation
    if (relationHandlingLevel === null) return null;
    return { id, relationHandlingLevel, relationHandlingLevelClarification };
  };

  searchRelations = searchValue => {
    const { includeInactive, types, crmTypes, take, activeOn } = this.props;
    const params = {
      searchValue,
      take,
      includeInactive: includeInactive || !!activeOn,
    };

    if (types && types.length) params.type = types;
    if (crmTypes && crmTypes.length) params.crmTypes = crmTypes;
    if (activeOn) params.activeOn = moment.utc(activeOn).format('YYYY-MM-DD');
    if (!searchValue)
      return Promise.resolve(
        (RecentRelationsService.getFiltered(params) || []).map(this.mapRelation),
      );

    const endpoint = `${config.appUrls.api}/api/relation/names`;

    return api.base.get(endpoint, { params }).then(result => result.data.map(this.mapRelation));
  };

  componentDidUpdate(prevProps) {
    const { initialSelectedItem, intl } = this.props;
    if (JSON.stringify(prevProps.initialSelectedItem) !== JSON.stringify(initialSelectedItem)) {
      this.setRelationStateFromInitialItem(initialSelectedItem);
    }
  }

  componentDidMount() {
    const { initialSelectedItem } = this.props;

    this.setRelationStateFromInitialItem(initialSelectedItem);
  }

  componentWillUnmount() {
    this.nameMapper = null;
  }

  setRelationStateFromInitialItem(initialSelectedItem) {
    if (!initialSelectedItem) {
      this.setState({ initialRelation: null, handlingLevel: null, retrievingRelation: false });
      return;
    }

    if (initialSelectedItem.id && initialSelectedItem.name) {
      this.setState({
        initialRelation: this.mapRelation(initialSelectedItem),
        handlingLevel: this.mapHandlingLevel(initialSelectedItem),
        retrievingRelation: false,
      });
      return;
    }
    if (!initialSelectedItem.label || initialSelectedItem.relationHandlingLevel == null) {
      this.setState({ retrievingRelation: true }, () => {
        api.base
          .post(`${config.appUrls.api}/api/relation/names`, [initialSelectedItem.key])
          .then(({ data: [relation] }) => {
            const { changeOnInit, onChange } = this.props;
            if (!relation) {
              this.setState({ retrievingRelation: false });
              return;
            }
            const initialRelation = { ...initialSelectedItem, ...this.mapRelation(relation) };
            this.setState({
              initialRelation,
              handlingLevel: this.mapHandlingLevel(initialRelation),
              retrievingRelation: false,
            });
            if (changeOnInit) {
              onChange(initialRelation);
            }
          });
      });
    } else {
      this.setState({
        initialRelation: initialSelectedItem,
        handlingLevel: this.mapHandlingLevel(initialSelectedItem),
        retrievingRelation: false,
      });
    }
  }

  setRelationAndTriggerChange = relation => {
    const { onChange } = this.props;
    this.setState(
      {
        initialRelation: relation,
        handlingLevel: this.mapHandlingLevel(relation),
      },
      () => onChange(relation),
    );
  };
  onRelationChanged = relation => {
    if (relation === undefined) return;
    if (relation === null) {
      this.setRelationAndTriggerChange(null);
      return;
    }
    if (relation.invoiceStreamId) {
      this.setRelationAndTriggerChange(relation);
      return;
    }
    // Recent relation selected -> retrieve extra info
    return api.base
      .post(`${config.appUrls.api}/api/relation/Names`, [relation.id])
      .then(({ data: [rel] }) => {
        if (rel) {
          this.setRelationAndTriggerChange(this.mapRelation(rel));
        }
      });
  };

  render() {
    const { placeholder, intl, disabled, emptyLabel, autoFocus, small } = this.props;
    const { initialRelation, retrievingRelation, handlingLevel } = this.state;
    return (
      !retrievingRelation && ( // Wait for initialRelation to be initialized (name lookup)
        <AutoComplete
          loadFunc={searchValue => this.searchRelations(searchValue)}
          loadOnInit
          placeholder={placeholder || intl.formatMessage(messages.SearchRelation)}
          onChange={item => this.onRelationChanged(item)}
          disabled={disabled}
          emptyLabel={emptyLabel}
          mode="edit"
          initialSelectedItem={initialRelation}
          showAll
          autoFocus={autoFocus}
          isSmall={small}
          renderItem={item => (
            <span>
              <RelationTypeIcon type={item.type} crm={item.crm} isActive={item.isActive} />
              <RelationHandlingLevel
                handlingLevel={item.relationHandlingLevel}
                clarification={item.relationHandlingLevelClarification}
              />
              {item.label} {item.alias && item.alias !== item.label && ` (${item.alias})`}
            </span>
          )}
          inputGroupItemLeft={
            handlingLevel && handlingLevel.relationHandlingLevel > 1 ? (
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={
                  <Tooltip id="relation-handling-tooltip">
                    {handlingLevel.relationHandlingLevelClarification}
                  </Tooltip>
                }
              >
                <span className="input-group-addon">
                  <i
                    className={classNames('fa-fw fa fa-exclamation-triangle', {
                      'text-warning': handlingLevel.relationHandlingLevel === 2,
                      'text-danger': handlingLevel.relationHandlingLevel === 3,
                    })}
                  />
                </span>
              </OverlayTrigger>
            ) : null
          }
        />
      )
    );
  }

  static propTypes = {
    placeholder: PropTypes.string,
    includeInactive: PropTypes.bool,
    types: PropTypes.arrayOf(PropTypes.number),
    crmTypes: PropTypes.arrayOf(PropTypes.number),
    // Restricts results to relations that had not ended yet on this date
    activeOn: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.object]),
    take: PropTypes.number,
    intl: intlShape.isRequired,

    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    emptyLabel: PropTypes.string,
    initialSelectedItem: PropTypes.oneOfType([
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
      }),
      PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired }),
    ]),
    changeOnInit: PropTypes.bool, // Trigger onchange after retrieving relation name
    autoFocus: PropTypes.bool,
    small: PropTypes.bool,
  };

  static defaultProps = {
    placeholder: null,
    includeInactive: false,
    types: [],
    crmTypes: [],
    activeOn: null,
    take: 15,
    disabled: false,
    emptyLabel: null,
    initialSelectedItem: null,
    changeOnInit: false,
    autoFocus: false,
    small: false,
  };
}

export default injectIntl(RelationSearch);
